import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getArticlesByCategory, getCategoryBySlug, categories } from "../../mocks/news";
import { usePageMeta, useJsonLd } from "../../hooks/useSeo";
import { MediaRow } from "../../components/base/NewsCard";

type SortKey = "latest" | "hot" | "featured";

const SORTS: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新发布" },
  { key: "hot", label: "热度最高" },
  { key: "featured", label: "编辑精选" },
];

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const cat = getCategoryBySlug(slug);
  const [sort, setSort] = useState<SortKey>("latest");

  const byCat = useMemo(() => getArticlesByCategory(slug), [slug]);
  const list = useMemo(() => {
    let arr = [...byCat];
    if (sort === "hot") arr = arr.sort((a, b) => Number(b.views.replace("万", "")) - Number(a.views.replace("万", "")));
    if (sort === "featured") {
      const f = arr.filter((a) => a.featured);
      arr = f.length ? f : arr.slice(0, 3);
    }
    return arr;
  }, [byCat, sort]);

  usePageMeta({
    title: cat
      ? `${cat.name} - 每日爆料网 ${cat.name}热点独家内容`
      : "栏目不存在 - 每日爆料网",
    description: cat
      ? `每日爆料网${cat.name}栏目：${cat.desc}。第一手娱乐资讯、网红动态与独家追踪。`
      : "你访问的栏目不存在，请返回首页。",
  });

  useJsonLd("category-breadcrumb", cat
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://29nx.cn/" },
          { "@type": "ListItem", position: 2, name: cat.name },
        ],
      }
    : null);

  useJsonLd("category-collection", cat
    ? {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${cat.name} - 每日爆料网`,
        description: cat.desc,
        url: `https://29nx.cn/category/${cat.slug}`,
        inLanguage: "zh-CN",
        isPartOf: { "@type": "WebSite", name: "每日爆料网" },
      }
    : null);

  if (!cat) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-serif text-3xl font-black text-foreground-950">栏目走丢了</h1>
        <p className="mt-3 text-foreground-600">这个栏目不存在，可能是链接有误。</p>
        <Link to="/" className="mt-6 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
          返回首页 <i className="ri-arrow-right-line" />
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* 栏目横幅 */}
      <section className="relative overflow-hidden">
        <img src={cat.cover} alt={`${cat.name}栏目封面`} title={`${cat.name} - 每日爆料网`} className="h-52 w-full object-cover md:h-64" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
        <div className="absolute inset-0">
          <div className="mx-auto flex h-full max-w-7xl flex-col justify-center px-4 md:px-6">
            <nav className="flex flex-wrap items-center gap-2 text-xs text-background-200/80">
              <Link to="/" className="hover:text-accent-300">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span>{cat.name}</span>
            </nav>
            <h1 className="mt-2 font-serif text-3xl font-black text-background-50 md:text-5xl">
              {cat.name}
            </h1>
            <p className="mt-2 flex items-center gap-2 text-sm text-background-100/85">
              <i className="ri-magic-line text-accent-300" /> {cat.tagline}
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          {/* 列表主体 */}
          <div className="lg:col-span-2">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 p-1">
              <p className="hidden px-3 text-sm text-foreground-600 sm:block">
                共 <span className="font-bold text-primary-600">{byCat.length}</span> 篇精选内容
              </p>
              <div className="flex rounded-md bg-background-100 p-1">
                {SORTS.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSort(s.key)}
                    className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      sort === s.key ? "bg-background-50 text-primary-600 shadow-sm" : "text-foreground-700 hover:text-primary-600"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            {list.map((item) => (
              <MediaRow key={item.slug} item={item} />
            ))}
            {list.length === 0 && (
              <p className="py-16 text-center text-foreground-500">该栏目内容更新中，敬请期待。</p>
            )}
          </div>

          {/* 侧栏 */}
          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-serif text-lg font-black text-foreground-950">
                <i className="ri-folder-2-line text-accent-500" /> 其他栏目
              </h2>
              <ul className="mt-3 divide-y divide-background-200">
                {categories.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/category/${c.slug}`}
                      className={`flex items-center justify-between py-3 text-sm transition-colors ${
                        c.slug === slug ? "font-bold text-primary-600" : "text-foreground-800 hover:text-primary-600"
                      }`}
                    >
                      {c.name}
                      <i className="ri-arrow-right-s-line opacity-50" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-secondary-800 p-5 text-background-100">
              <h2 className="flex items-center gap-2 font-serif text-lg font-black text-accent-300">
                <i className="ri-hand-coin-line" /> 线索投稿
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-background-200">
                有独家线索？欢迎投稿，我们将第一时间核实与跟进。
              </p>
              <Link to="/faq#tougao" className="mt-3 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-accent-400 px-4 py-2 text-sm font-medium text-secondary-900 hover:bg-accent-300">
                去投稿 <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}